/**
 * Dashboard — grouping and selection model (pure, React-free).
 *
 * The overview payload has one row per ProduktInstanz. The page shows those
 * rows folded into groups, chosen by the operator:
 *
 *   - `kunde`   — one group per Kunde: "what do I have to tell this customer?",
 *   - `produkt` — one group per ProduktType: "which deliveries of this product
 *                  are exposed, and at which customers?".
 *
 * The notify gate is a cross-payload join (see `notifyGate` in
 * dashboardModel.ts); this module takes the already computed gates as a map so
 * it stays free of runtime imports and can be tested with plain node.
 *
 * Work sections — each instance is in exactly one, decided by its gate:
 *   - `assess`   KEV hit without a confirmed product verdict (or no event yet),
 *   - `notify`   a confirmed AFFECTED created notifications still to be sent,
 *   - `notified` every notification of the instance went out,
 *   - `none`     assessed as not applicable, or no KEV hit at all.
 */

import type { OverviewRow } from '../../lib/types';
import type { NotifyGate } from './dashboardModel';

export type GroupMode = 'kunde' | 'produkt';

export type GateMap = ReadonlyMap<number, NotifyGate>;

export type WorkState = 'assess' | 'notify' | 'notified' | 'none';

/** The work section of one instance. See the module comment. */
export function workState(gate: NotifyGate | undefined): WorkState {
  if (gate === undefined || gate.kind === 'clean') return 'none';
  if (gate.kind === 'notified') return 'notified';
  if (gate.kind === 'ready') return 'notify';
  // No event yet means the KEV scan still has to raise one: not assessed either.
  return gate.openEvents > 0 || gate.totalEvents === 0 ? 'assess' : 'none';
}

export interface OverviewGroup {
  /** Unique across both modes (`k:` / `p:` prefix), stable across refreshes. */
  key: string;
  mode: GroupMode;
  /** Kunde name or ProduktType designation. */
  title: string;
  /** Kunde: contact e-mail; Produkt: Modellreihe. */
  subtitle: string | null;
  /** Kunde mode only — CRA reporting duties apply. */
  euKunde: boolean;
  /** Kunde mode only — false when the instances carry no KundeID. */
  hasKunde: boolean;
  /** The instances shown in the group — in a section only those of the section. */
  rows: OverviewRow[];
  /** All instances of the customer/product, independent of the section. */
  total: number;
  /** Instances with at least one KEV-affected component. */
  affected: number;
  /** Sorted union of the KEV CVEs of all instances. */
  cves: string[];
  /** Distinct counterparts: product types (Kunde mode) or customers (Produkt mode). */
  counterparts: number;
  notified: number;
  ready: number;
  /** KEV hit without a confirmed product verdict. */
  toAssess: number;
  /** Instances that need (or may still need) a notification: notified + ready + toAssess. */
  due: number;
}

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function groupKey(row: OverviewRow, mode: GroupMode): string {
  if (mode === 'kunde') {
    return row.kunde.id !== null ? `k:${row.kunde.id}` : `k:none:${row.produkt.id}`;
  }
  if (row.produkttyp.id !== null) return `p:${row.produkttyp.id}`;
  const name = clean(row.produkttyp.bezeichnung).toLowerCase();
  return name === '' ? 'p:none' : `p:name:${name}`;
}

/** Fold instance rows into groups. Order: most to notify first, then name. */
export function groupRows(
  rows: readonly OverviewRow[],
  mode: GroupMode,
  gates: GateMap,
): OverviewGroup[] {
  const byKey = new Map<string, OverviewRow[]>();
  for (const row of rows) {
    const key = groupKey(row, mode);
    const bucket = byKey.get(key);
    if (bucket === undefined) byKey.set(key, [row]);
    else bucket.push(row);
  }

  const groups: OverviewGroup[] = [];
  for (const [key, members] of byKey) {
    const first = members[0];
    if (first === undefined) continue;
    const cves = new Set<string>();
    const counterparts = new Set<string>();
    let affected = 0;
    let notified = 0;
    let ready = 0;
    let toAssess = 0;
    for (const row of members) {
      if (row.affected_count > 0) affected += 1;
      for (const cve of row.kev_cves) cves.add(cve);
      counterparts.add(
        mode === 'kunde'
          ? String(row.produkttyp.id ?? clean(row.produkttyp.bezeichnung))
          : String(row.kunde.id ?? `none:${row.produkt.id}`),
      );
      const state = workState(gates.get(row.produkt.id));
      if (state === 'notified') notified += 1;
      else if (state === 'notify') ready += 1;
      else if (state === 'assess') toAssess += 1;
    }

    const title =
      mode === 'kunde'
        ? clean(first.kunde.name) || 'Unbekannter Kunde'
        : clean(first.produkttyp.bezeichnung) || 'Unbekannter Produkttyp';
    const subtitle =
      mode === 'kunde' ? clean(first.kunde.email) || null : clean(first.produkttyp.modellreihe) || null;

    groups.push({
      key,
      mode,
      title,
      subtitle,
      euKunde: mode === 'kunde' && first.kunde.ist_eu,
      hasKunde: first.kunde.id !== null,
      rows: members,
      total: members.length,
      affected,
      cves: [...cves].sort(),
      counterparts: counterparts.size,
      notified,
      ready,
      toAssess,
      due: notified + ready + toAssess,
    });
  }

  return groups.sort(
    (a, b) =>
      b.ready - a.ready ||
      b.affected - a.affected ||
      a.title.localeCompare(b.title, 'de', { sensitivity: 'base' }),
  );
}

/**
 * The groups of one work section, each with only its instances of that
 * section. Most instances of the section first, then name.
 */
export function sectionGroups(
  groups: readonly OverviewGroup[],
  state: WorkState,
  gates: GateMap,
): OverviewGroup[] {
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) => workState(gates.get(row.produkt.id)) === state),
    }))
    .filter((group) => group.rows.length > 0)
    .sort(
      (a, b) =>
        b.rows.length - a.rows.length ||
        a.title.localeCompare(b.title, 'de', { sensitivity: 'base' }),
    );
}

/**
 * Audit-Log view of the Versand entries of one customer — where an already
 * sent advisory is looked up instead of being sent again.
 */
export function auditLogLink(row: OverviewRow): string {
  const params = new URLSearchParams({ gruppe: 'meldungen' });
  const kunde = clean(row.kunde.name);
  if (kunde !== '') params.set('q', kunde);
  return `/audit-log?${params.toString()}`;
}
